import { TemplateRef, Type } from '@angular/core';
import { BaseModel } from '@kinet-open-network/client/shared/data-access/model';
import { AbilitySubject } from '@kinet-open-network/shared/data-access/model';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyFieldProps,
} from '@ngx-formly/core';
import { FormlyInputFieldConfig } from '@ngx-formly/primeng/input';
import { FormlyRadioFieldConfig } from '@ngx-formly/primeng/radio';
import { FormlyTextAreaFieldConfig } from '@ngx-formly/primeng/textarea';
import { Observable } from 'rxjs';
import { FormlyCheckboxFieldConfig } from '../types/checkbox/checkbox-type.component';
import { FormlyColorPickerFieldConfig } from '../types/colorpicker/colorpicker-type.component';
import { FormlyCustomTemplateFieldConfig } from '../types/custom-template/custom-template-type.component';
import { FormlyEditorFieldConfig } from '../types/editor/editor-type.component';
import { FormlyGalleryFieldConfig } from '../types/gallery/gallery-type.component';
import { FormlyMultiselectFieldConfig } from '../types/multiselect/multiselect-type.component';
import { FormlyPriceConditionsFieldConfig } from '../types/price-conditions/price-conditions-type.component';
import { FormlyRepeatFieldConfig } from '../types/repeat/repeat-type.component';
import { FormlySelectFieldConfig } from '../types/select/select-type.component';
import {
  FieldBuilderArray,
  FieldBuilderBase,
  FieldBuilderGroup,
} from './field-builder-base';
import { FieldBuilderDatepicker } from './field-builder-datepicker';
import { FieldBuilderRepeatTable } from './field-builder-repeat-table';
import {
  FieldBuilderMultiselectLazy,
  FieldBuilderSelectLazy,
} from './field-builder-select-lazy';
import { FieldBuilderTabs, FieldBuilderTabsRepeat } from './field-builder-tabs';

export const formBuilder = (
  fields: FieldBuilderBase[] | FieldBuilderBase,
): FormlyFieldConfig[] =>
  Array.isArray(fields)
    ? fields.map((field) => field.build())
    : [fields.build()];

export const field = () => new FieldBuilderBase<FormlyFieldConfig>();

// type: input
export const input = (key: string, label: string) =>
  new FieldBuilderBase<FormlyInputFieldConfig>({
    type: 'input',
    key,
    props: { label },
  });
// alias for field type input
export const text = input;

// type: input
export const radio = (key: string, label: string, inline = true) =>
  new FieldBuilderBase<FormlyRadioFieldConfig>(
    {
      type: 'radio',
      key,
      props: { label },
    },
    {
      className: inline
        ? '[&_formly-field-primeng-radio]:inline-flex [&_formly-field-primeng-radio]:gap-4'
        : '',
    },
  );

// type: number
export const number = (
  key: string,
  label: string,
  min = 0,
  max = 100000,
  step = 1,
) =>
  new FieldBuilderBase({
    type: 'number',
    key,
    props: { label, min, max, step },
  });

// type: password
export const password = (key: string, label: string) =>
  new FieldBuilderBase({ type: 'password', key, props: { label } });

export const datepicker = (key: string, label: string) =>
  new FieldBuilderDatepicker({
    key,
    props: { label },
  });

// type: textarea
export const textarea = (key: string, label: string) =>
  new FieldBuilderBase<FormlyTextAreaFieldConfig>({
    type: 'textarea',
    key,
    props: { label },
  });

// type: editor
export const editor = (key: string, label: string) =>
  new FieldBuilderBase<FormlyEditorFieldConfig>({
    type: 'editor',
    key,
    props: { label },
  });

// type: checkbox
export const checkbox = (key: string, label: string) =>
  new FieldBuilderBase<FormlyCheckboxFieldConfig>({
    type: 'checkbox',
    key,
    props: { label },
  });

// type: divider
export const divider = () => new FieldBuilderBase({ type: 'divider' });

// type: template
export const html = (template: string) => new FieldBuilderBase({ template });

// type: template
export const header = (
  label: string,
  subtitle?: string,
  config?: { tag?: string; className?: string },
) => {
  let template = `<${config?.tag || 'h3'} class="${config?.className || ''}">${label}</${config?.tag || 'h3'}>`;

  if (subtitle) {
    template += `<p class="text-sm text-gray-500">${subtitle}</p>`;
  }

  return html(template).className('block col-span-full mb-3');
};

// type: phone-number
export const phoneNumber = (key: string, label: string) =>
  new FieldBuilderBase({ type: 'phone-number', key, props: { label } });

// type: select
export const select = (key: string, label?: string) =>
  new FieldBuilderBase<FormlySelectFieldConfig>({
    type: 'select',
    key,
    props: { label },
  });

// type: multiselect
export const multiSelect = (key: string, label: string) =>
  new FieldBuilderBase<FormlyMultiselectFieldConfig>({
    type: 'multiselect',
    key,
    props: { label },
  });

//type: select-lazy
export const selectLazy = <T, M = any>(
  key: string,
  label: string,
  labelProp: keyof T | string,
  valueProp: keyof T | string,
  initValueField?: keyof M,
) =>
  new FieldBuilderSelectLazy<T>({
    key,
    props: {
      label,
      valueProp: valueProp as string,
      labelProp: labelProp as string,
      initValueField: initValueField as string,
    },
  });

// type: multiselect-lazy
export const multiselectLazy = <T extends BaseModel<unknown, AbilitySubject>>(
  key: string,
  label: string,
  valueProp: keyof T,
  labelProp: keyof T,
) =>
  new FieldBuilderMultiselectLazy<T>({
    key,
    props: {
      label,
      labelProp: labelProp as string,
      valueProp: valueProp as string,
    },
  });

// type: tabs
export const tabs = (key?: string) => new FieldBuilderTabs({ key });

// type: tabs-repeat
export const tabsRepeat = (key: string) => new FieldBuilderTabsRepeat({ key });

// type: repeat
export const repeat = (key: string) =>
  new FieldBuilderArray<FormlyRepeatFieldConfig>({
    type: 'repeat',
    key,
  });

// type: repeat-table
export const repeatTable = (key?: string) =>
  new FieldBuilderRepeatTable({
    key,
  });

// type: accordion
export const accordion = (key?: string) =>
  new FieldBuilderBase({
    type: 'accordion',
    key,
  });

// type: colorpicker
export const colorpicker = (key: string, label?: string) =>
  new FieldBuilderBase<FormlyColorPickerFieldConfig>({
    type: 'colorpicker',
    key,
    props: { label },
  });

// type: gallery
export const gallery = (key: string, label?: string, accept?: string) =>
  new FieldBuilderBase<FormlyGalleryFieldConfig>({
    type: 'gallery',
    key,
    props: { label, accept },
  });

// type: price conditions
export const priceConditions = (key: string, label?: string) =>
  new FieldBuilderBase<FormlyPriceConditionsFieldConfig>({
    type: 'price-conditions',
    key,
    props: { label },
  });

export const fieldGroup = (label?: string) =>
  new FieldBuilderGroup({ props: { label } });

export const customField = <Props extends FormlyFieldProps>(
  key: string,
  fieldType: Type<FieldType<FieldTypeConfig<Props>>>,
) =>
  new FieldBuilderBase<FormlyFieldConfig<Props>>({
    type: fieldType,
    key,
  });

export const customTemplate = (
  key: string,
  template?: Observable<TemplateRef<any> | undefined>,
) =>
  new FieldBuilderBase<FormlyCustomTemplateFieldConfig>({
    key,
    type: 'custom-template',
    props: {
      customTemplate: template,
    },
  });
